import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SkeletonBar, SkeletonRows } from "../components/ui/Skeleton.jsx";
import Tabs from "../components/ui/Tabs.jsx";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import {
    RELEASE_ENDPOINT_ACTION,
    RELEASE_STATE,
    abandonManagedAgentConfigRelease,
    createSaveNonce,
    getManagedAgentConfig,
    getManagedAgentConfigRelease,
    getManagedAgentConfigVersion,
    getManagedConfigControlCatalog,
    getManagedConfigFleetSummary,
    listManagedAgentConfigPilotEndpointStates,
    listManagedAgentConfigVersions,
    listManagedConfigEndpointReasons,
    previewManagedConfigReachability,
    setManagedAgentConfigEnabled,
    signManagedAgentConfigRelease,
    startManagedAgentConfigPilot,
    unmanageManagedAgentConfig,
    updateManagedAgentConfigReleaseEndpoint,
} from "../lib/managedConfigClient.js";
import { AGENT_DEFINITIONS } from "./agentPolicy/agents.js";
import { REJECTION_COPY } from "./agentPolicy/claudeCode/copy.js";
import {
    changedPolicyCategories,
    changedTopLevelKeys,
    composeManagedAgentConfig,
    controlsFromDocument,
} from "./agentPolicy/claudeCode/document.js";
import { organizationUuidError } from "./agentPolicy/claudeCode/SessionSetup.jsx";
import { INITIAL_RULE_COMPOSER } from "./agentPolicy/claudeCode/ToolPermissions.jsx";
import {
    managedConfigDifferences,
    managedConfigDocumentChanges,
    mergeManagedConfigDocuments,
} from "./agentPolicy/documentDiff.js";
import { documentLeafChanges, tallyChanges } from "./agentPolicy/changeTally.js";
import { StatusMark } from "./agentPolicy/controls.jsx";
import { formatAuthor } from "./agentPolicy/format.js";
import { removalStatus } from "./agentPolicy/removalStatus.js";
import { SECONDARY_ACTION } from "./agentPolicy/styles.js";
import StepStrip, { currentReleaseStep, plural, releaseSteps } from "./agentPolicy/StepStrip.jsx";
import PolicyEditor, { visibleSubsections } from "./agentPolicy/PolicyEditor.jsx";
import ChoosePilotEndpoints from "./agentPolicy/ChoosePilotEndpoints.jsx";
import PilotRelease from "./agentPolicy/PilotRelease.jsx";
import ReleasesView from "./agentPolicy/ReleasesView.jsx";
import FleetCompliance from "./agentPolicy/FleetCompliance.jsx";
import AllAgentsOverview from "./agentPolicy/AllAgentsOverview.jsx";

export {
    changedPolicyCategories,
    changedTopLevelKeys,
    composeManagedAgentConfig,
    controlsFromDocument,
    formatAuthor,
    managedConfigDifferences,
    managedConfigDocumentChanges,
    mergeManagedConfigDocuments,
    organizationUuidError,
};

// How often the open pilot table re-reads endpoint evidence. Pilot endpoints
// report on their heartbeat, so a shorter interval mostly re-reads unchanged rows.
const PILOT_REFRESH_MS = 15_000;

const VIEWS = Object.freeze([
    { id: "policy", label: () => "Policy" },
    { id: "releases", label: () => "Releases" },
    { id: "fleet", label: (fleet) => `Fleet compliance${fleet?.knownTotal || fleet?.total ? ` (${fleet.knownTotal ?? fleet.total})` : ""}` },
]);

// Everything that belongs to one agent. Switching the agent tab keeps each
// agent's draft, because each agent has its own entry.
function initialAgentState(definition) {
    return {
        loading: true,
        controls: definition.emptyControls(),
        catalog: null,
        preview: null,
        fleet: null,
        current: null,
        enabled: true,
        history: [],
        knownDocuments: {},
        release: null,
        pilotEndpointLimit: 0,
        pilotStates: [],
        pilotStatesLoading: false,
        pendingSubmission: null,
        pendingRemoval: null,
        pendingAbandon: null,
        choosing: false,
        pilotSelection: [],
        editorDuringPilot: false,
        category: definition.categories[0].id,
        ui: { ruleComposer: { ...INITIAL_RULE_COMPOSER }, autoRuleKind: "hardDeny" },
    };
}

function parseDocument(text) {
    try { return text ? JSON.parse(text) : {}; }
    catch { return {}; }
}

function openRelease(release) {
    return release?.state === RELEASE_STATE.PILOTING ? release : null;
}

function TitleRow({ eyebrow, title, subtitle, fields = [], delivery }) {
    return (
        <header className="flex flex-wrap items-end justify-between gap-[12px]">
            <div className="grid gap-[3px]">
                {eyebrow ? <span className={EYEBROW_CLASS}>{eyebrow}</span> : null}
                <h1 className="m-0 text-[20px] leading-[1.08] font-medium tracking-[-0.04em] text-[color:var(--ink)]">{title}</h1>
                {subtitle ? <p className={`${NOTE_CLASS} m-0 text-[12px]`}>{subtitle}</p> : null}
            </div>
            <div className="flex flex-wrap items-end gap-[24px]">
                {fields.length ? (
                    <dl className="m-0 flex flex-wrap gap-[24px] text-[12px]">
                        {fields.map((field) => <div key={field.label} className="grid gap-[3px]"><dt className="text-[12px] leading-[1.2] text-[color:var(--muted)]">{field.label}</dt><dd className="m-0 text-[color:var(--ink)]">{field.value}</dd></div>)}
                    </dl>
                ) : null}
                {delivery}
            </div>
        </header>
    );
}

// A line not yet read: a bar one line of the surrounding text tall (the
// zero-width space sets the line), so the text lands without moving anything.
function PendingLine({ className, label = null }) {
    return <span className="flex items-center">{"\u200b"}<SkeletonBar className={className} />{label ? <span className="sr-only">{label}</span> : null}</span>;
}

// PolicyEditor's three columns while the agent's policy is read: the category
// list, the open category's sections and the draft tally stand as placeholders,
// so the editor lands in place and nothing can be edited before the stored
// policy fills it.
function EditorLoading({ definition, category, controls }) {
    const active = definition.categories.find((item) => item.id === category) ?? definition.categories[0];
    return (
        <div className="grid items-start gap-[16px] xl:grid-cols-[230px_minmax(0,1fr)_290px]" aria-hidden="true">
            <div className="hidden self-start rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] xl:block">
                {definition.categories.map((item, index) => <div key={item.id} className={`flex h-[38px] items-center px-[12px] ${index ? "border-t border-[color:var(--line)]" : ""}`}><SkeletonBar className="w-[60%]" /></div>)}
            </div>
            <div className="grid min-w-0 gap-[12px]">
                <p className={`${NOTE_CLASS} m-0`}>Unconfigured settings keep their existing behavior.</p>
                <div className="grid gap-[5px] xl:hidden"><span className="text-[11px] font-medium text-[color:var(--ink)]">Policy category</span><span className="min-h-[32px] rounded-[7px] border border-[color:var(--line)]" /></div>
                <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{active.label}</h2>
                {visibleSubsections(active, controls).map((subsection, index) => (
                    <div key={subsection.id} className="flex min-h-[46px] items-center rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[36px]" style={{ opacity: Math.max(0.25, 1 - index * 0.12) }}><SkeletonBar className="w-[40%]" /></div>
                ))}
            </div>
            <div className="grid content-start gap-[12px] self-start rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]">
                <span className={EYEBROW_CLASS}>Draft</span>
                <SkeletonBar className="w-[60%]" />
                <SkeletonBar className="w-[80%]" />
            </div>
        </div>
    );
}

// The Releases view while the agent's history is read, so "No policy history
// yet" is said only once it is true.
function ReleasesLoading() {
    return (
        <div className="grid gap-[8px]">
            <header className="flex flex-wrap items-center justify-between gap-[10px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px]">
                <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">Releases</h2>
            </header>
            <div className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]">
                <SkeletonRows rows={3} cells={["w-[28px]", "w-[60%]", "w-[72px]"]} rowClassName="grid h-[64px] grid-cols-[70px_minmax(0,1fr)_auto] items-center gap-[12px] px-[12px]" label="Loading policy history…" />
            </div>
        </div>
    );
}

function TabRow({ label, tabs, active, onChange }) {
    return <Tabs label={label} items={tabs} value={active} onChange={onChange} />;
}

export default function ManagedAgentConfigPage({ agents = AGENT_DEFINITIONS, initialAgentId = "all", initialReleaseId = "" }) {
    const caps = useMyPermissions();
    const canManage = hasPermission(caps, PERMISSIONS.AGENT_CONFIG_MANAGE);
    const user = useCurrentUser();
    const [agentId, setAgentId] = useState(agents.some((a) => a.id === initialAgentId) ? initialAgentId : "all");
    const definition = agents.find((agent) => agent.id === agentId) ?? agents[0];
    const [store, setStore] = useState({});
    const fallback = useMemo(() => initialAgentState(definition), [definition]);
    const agent = store[definition.id] ?? fallback;
    const [view, setView] = useState("policy");
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [rejections, setRejections] = useState([]);
    const [showDocument, setShowDocument] = useState(false);
    const [fleetLoading, setFleetLoading] = useState(false);
    const [fleetCategory, setFleetCategory] = useState("all");
    const [endpoints, setEndpoints] = useState([]);
    const [endpointCursor, setEndpointCursor] = useState("");
    const [endpointsLoading, setEndpointsLoading] = useState(false);
    const [expandedEndpointId, setExpandedEndpointId] = useState("");
    const [expandedVersionId, setExpandedVersionId] = useState("");
    const [historyDocumentLoading, setHistoryDocumentLoading] = useState("");
    const [historyDocumentErrors, setHistoryDocumentErrors] = useState({});
    const [releaseDetails, setReleaseDetails] = useState({});
    const mounted = useRef(true);
    const startedLoads = useRef(new Set());

    const patchAgent = useCallback((id, patch) => setStore((all) => {
        const previous = all[id] ?? initialAgentState(agents.find((item) => item.id === id) ?? agents[0]);
        const next = typeof patch === "function" ? patch(previous) : { ...previous, ...patch };
        return { ...all, [id]: next };
    }), [agents]);

    const { controls, catalog, current, enabled, release, preview } = agent;
    const piloting = Boolean(openRelease(release));
    const canEdit = canManage && !piloting;
    const candidate = useMemo(() => definition.compose(controls), [definition, controls]);
    const candidateDocument = useMemo(() => JSON.stringify(candidate), [candidate]);
    const currentDocument = useMemo(() => parseDocument(current?.document), [current]);
    const pilotDocument = useMemo(() => parseDocument(release?.version?.document), [release]);
    const changedKeys = useMemo(() => changedTopLevelKeys(currentDocument, candidate), [currentDocument, candidate]);
    const tally = useMemo(
        () => tallyChanges(definition, documentLeafChanges(currentDocument, candidate, { memberPointers: definition.memberPointers })),
        [definition, currentDocument, candidate],
    );
    const pilotTally = useMemo(
        () => tallyChanges(definition, documentLeafChanges(currentDocument, pilotDocument, { memberPointers: definition.memberPointers })),
        [definition, currentDocument, pilotDocument],
    );
    const derived = useMemo(() => definition.derive(controls, catalog), [definition, controls, catalog]);
    const incomplete = useMemo(() => definition.incompleteLists(controls), [definition, controls]);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const updateControls = (updater) => {
        patchAgent(definition.id, (state) => ({
            ...state,
            controls: definition.clone(typeof updater === "function" ? updater(state.controls) : updater),
            pendingSubmission: null,
        }));
        setMessage(null);
        setRejections([]);
    };
    const setUi = (updater) => patchAgent(definition.id, (state) => ({ ...state, ui: typeof updater === "function" ? updater(state.ui) : updater }));
    const setCategory = (category) => patchAgent(definition.id, { category });

    const reloadVersions = async (def) => {
        try {
            const history = await listManagedAgentConfigVersions(def.vendor);
            patchAgent(def.id, { history });
        } catch { patchAgent(def.id, { history: [] }); }
    };

    // The summary on screen stays while it is re-read; a new observation re-reads
    // the endpoints, and a failed read drops the summary rather than pass it off
    // as current.
    const reloadFleet = async (def, reportError = false) => {
        setFleetLoading(true);
        try {
            const fleet = await getManagedConfigFleetSummary(def.vendor, { agentLabel: def.label });
            patchAgent(def.id, { fleet });
        } catch {
            patchAgent(def.id, { fleet: null });
            if (reportError) setMessage({ tone: "warning", text: "Fleet status could not be refreshed. Try again." });
        } finally {
            setFleetLoading(false);
        }
    };

    // The tenant's current generation, loaded into the editor. Returns false
    // when it could not be read.
    const reloadCurrent = async (def) => {
        try {
            const refreshed = await getManagedAgentConfig(def.vendor);
            patchAgent(def.id, (state) => ({
                ...state,
                current: refreshed.currentVersion,
                enabled: refreshed.enabled,
                knownDocuments: refreshed.currentVersion?.document
                    ? { ...state.knownDocuments, [refreshed.currentVersion.versionId]: refreshed.currentVersion.document }
                    : state.knownDocuments,
                controls: def.fromDocument(parseDocument(refreshed.currentVersion?.document), state.catalog),
                pendingSubmission: null,
            }));
            return true;
        } catch {
            return false;
        }
    };

    // `quiet` is the background refresh: no loading state, and a failure waits for
    // the next tick instead of raising a warning.
    const loadPilotStates = async (def, releaseId, { quiet = false } = {}) => {
        if (!quiet) patchAgent(def.id, { pilotStatesLoading: true });
        try {
            const result = await listManagedAgentConfigPilotEndpointStates(def.vendor, releaseId);
            patchAgent(def.id, quiet ? { pilotStates: result.states } : { pilotStates: result.states, pilotStatesLoading: false });
        } catch {
            if (quiet) return;
            patchAgent(def.id, { pilotStatesLoading: false });
            setMessage({ tone: "warning", text: "Pilot endpoint evidence could not be loaded. Refresh to try again." });
        }
    };

    // Show a release that is piloting, with the editor locked to its document.
    const adoptRelease = (def, open) => patchAgent(def.id, (state) => ({
        ...state,
        release: open,
        pilotEndpointLimit: open.pilotEndpointLimit || state.pilotEndpointLimit,
        choosing: false,
        pilotSelection: [],
        editorDuringPilot: false,
        pendingSubmission: null,
        knownDocuments: open.version?.document
            ? { ...state.knownDocuments, [open.version.versionId]: open.version.document }
            : state.knownDocuments,
        controls: open.version?.document ? def.fromDocument(parseDocument(open.version.document), state.catalog) : state.controls,
    }));

    useEffect(() => {
        if (agentId === "all") return;
        const def = definition;
        if (startedLoads.current.has(def.id)) return;
        startedLoads.current.add(def.id);
        (async () => {
            const [catalogResult, fleetResult, configResult, historyResult, releaseResult] = await Promise.allSettled([
                getManagedConfigControlCatalog(def.vendor),
                getManagedConfigFleetSummary(def.vendor, { agentLabel: def.label }),
                getManagedAgentConfig(def.vendor),
                listManagedAgentConfigVersions(def.vendor),
                getManagedAgentConfigRelease(def.vendor, initialReleaseId && def.id === initialAgentId ? { releaseId: initialReleaseId } : undefined),
            ]);
            if (!mounted.current) return;
            const loadedCatalog = catalogResult.status === "fulfilled" ? catalogResult.value : null;
            const next = { loading: false, catalog: loadedCatalog };
            if (fleetResult.status === "fulfilled") {
                next.fleet = fleetResult.value;
                // The config read 404s until a version is released; the fleet
                // summary still carries the delivery switch a first pilot obeys.
                next.enabled = fleetResult.value.deliveryEnabled;
            }
            if (configResult.status === "fulfilled") {
                next.current = configResult.value.currentVersion;
                next.enabled = configResult.value.enabled;
                if (configResult.value.currentVersion?.document) {
                    next.knownDocuments = { [configResult.value.currentVersion.versionId]: configResult.value.currentVersion.document };
                    try { next.controls = def.fromDocument(JSON.parse(configResult.value.currentVersion.document), loadedCatalog); }
                    catch { setMessage({ tone: "danger", text: "The stored policy could not be displayed as controls." }); }
                }
            } else if (configResult.reason?.status !== 404 && configResult.reason?.grpcCode !== "NOT_FOUND") {
                setMessage({ tone: "warning", text: "The policy API is unavailable. Controls show an unsaved draft." });
            }
            if (historyResult.status === "fulfilled") next.history = historyResult.value;
            const targeted = initialReleaseId && def.id === initialAgentId;
            const loadedRelease = releaseResult.status === "fulfilled" ? releaseResult.value.release : null;
            const matchesTarget = !targeted || loadedRelease?.releaseId === initialReleaseId;
            const open = matchesTarget ? openRelease(loadedRelease) : null;
            if (targeted && !open) {
                setView("releases");
                setMessage({ tone: "warning", text: releaseResult.status === "rejected" || !matchesTarget
                    ? "This pilot could not be loaded. Open Releases to check its status."
                    : "This pilot is no longer open. Check its status in Releases." });
            }
            if (releaseResult.status === "fulfilled") next.pilotEndpointLimit = releaseResult.value.pilotEndpointLimit || open?.pilotEndpointLimit || 0;
            patchAgent(def.id, next);
            if (open) {
                adoptRelease(def, open);
                await loadPilotStates(def, open.releaseId);
            }
        })();
        // The load runs once for each agent; later reads go through the handlers.
    }, [agentId, definition, initialAgentId, initialReleaseId]);

    useEffect(() => {
        if (!canManage || agentId === "all") return undefined;
        const id = definition.id;
        let live = true;
        previewManagedConfigReachability(definition.vendor, candidateDocument).then((result) => {
            if (live) patchAgent(id, { preview: result });
        }).catch(() => { if (live) patchAgent(id, { preview: null }); });
        return () => { live = false; };
    }, [agentId, canManage, definition, candidateDocument, patchAgent]);

    const fleetObservedAt = agent.fleet?.observedAt;
    useEffect(() => {
        if (agentId !== "all" && view === "fleet" && !agent.loading) reloadFleet(definition);
    }, [agentId, view, agent.loading]);
    useEffect(() => {
        let live = true;
        setEndpoints([]);
        setEndpointCursor("");
        setExpandedEndpointId("");
        if (agentId === "all" || view !== "fleet" || !fleetObservedAt || (agent.fleet?.desiredGeneration === "0" && !openRelease(release))) return () => { live = false; };
        setEndpointsLoading(true);
        listManagedConfigEndpointReasons(definition.vendor, { category: fleetCategory, observedAt: fleetObservedAt })
            .then((result) => {
                if (!live) return;
                setEndpoints(result.endpoints);
                setEndpointCursor(result.nextCursor);
            })
            .catch(() => {
                if (live) setMessage({ tone: "warning", text: "Endpoint deployment details are unavailable." });
            })
            .finally(() => { if (live) setEndpointsLoading(false); });
        return () => { live = false; };
    }, [agentId, definition.vendor, fleetObservedAt, fleetCategory, view, agent.fleet?.desiredGeneration, release]);

    const loadMoreEndpoints = async () => {
        if (!endpointCursor || endpointsLoading) return;
        setEndpointsLoading(true);
        try {
            const result = await listManagedConfigEndpointReasons(definition.vendor, {
                category: fleetCategory,
                cursor: endpointCursor,
                observedAt: fleetObservedAt,
            });
            setEndpoints((items) => [...items, ...result.endpoints]);
            setEndpointCursor(result.nextCursor);
        } catch {
            setMessage({ tone: "warning", text: "The next endpoint page could not be loaded." });
        } finally {
            setEndpointsLoading(false);
        }
    };

    const selectAgent = (id) => {
        if (id === agentId) return;
        setAgentId(id);
        setMessage(null);
        setRejections([]);
        setShowDocument(false);
        setExpandedEndpointId("");
        setExpandedVersionId("");
    };

    const navigateFromOverview = (id, nextView, category) => {
        selectAgent(id);
        setView(nextView === "pilot" ? "policy" : nextView);
        if (nextView === "pilot") patchAgent(id, { editorDuringPilot: false });
        if (category) setFleetCategory(category);
    };

    const toggleHistoricalVersion = async (version) => {
        const def = definition;
        if (expandedVersionId === version.versionId) {
            setExpandedVersionId("");
            return;
        }
        setExpandedVersionId(version.versionId);
        if (version.releaseId && !releaseDetails[version.releaseId]) {
            getManagedAgentConfigRelease(def.vendor, { releaseId: version.releaseId })
                .then((result) => {
                    if (result.release) setReleaseDetails((details) => ({ ...details, [version.releaseId]: result.release }));
                })
                .catch(() => {});
        }
        if (agent.knownDocuments[version.versionId]) return;
        setHistoryDocumentLoading(version.versionId);
        setHistoryDocumentErrors((errors) => ({ ...errors, [version.versionId]: "" }));
        try {
            const stored = await getManagedAgentConfigVersion(def.vendor, version.versionId);
            if (!stored?.document) throw new Error("Historical document was absent");
            patchAgent(def.id, (state) => ({ ...state, knownDocuments: { ...state.knownDocuments, [version.versionId]: stored.document } }));
        } catch {
            setHistoryDocumentErrors((errors) => ({
                ...errors,
                [version.versionId]: "This stored policy could not be loaded. Try again.",
            }));
        } finally {
            setHistoryDocumentLoading("");
        }
    };

    const handleStartPilot = async () => {
        const def = definition;
        if (incomplete.length) {
            setMessage({ tone: "warning", text: "Add at least one entry to every selected list before starting a pilot." });
            return;
        }
        const expectedGeneration = current?.generation ?? "0";
        const endpointIds = agent.pilotSelection.map((endpoint) => endpoint.id);
        const pending = agent.pendingSubmission;
        const submission = pending?.document === candidateDocument
            && pending?.expectedGeneration === expectedGeneration
            && JSON.stringify(pending?.endpointIds) === JSON.stringify(endpointIds)
            ? pending
            : { document: candidateDocument, expectedGeneration, endpointIds, saveNonce: createSaveNonce() };
        patchAgent(def.id, { pendingSubmission: submission });
        setSaving(true);
        setMessage(null);
        setRejections([]);
        try {
            const result = await startManagedAgentConfigPilot(def.vendor, submission);
            if (result.rejections.length) {
                setRejections(result.rejections);
                setMessage({ tone: "danger", text: "A dashboard control produced a policy the server refused. No pilot was started." });
                patchAgent(def.id, { pendingSubmission: null, choosing: false });
            } else if (result.release) {
                const started = result.release;
                adoptRelease(def, started);
                setMessage({
                    tone: "good",
                    text: result.idempotentReplay
                        ? "The original pilot request succeeded. Its response was recovered."
                        : expectedGeneration === "0"
                            ? `Delivering v${started.version?.generation} to ${plural(started.endpoints.length, "pilot endpoint")}. No other endpoint receives a policy until you sign the release.`
                            : `Delivering v${started.version?.generation} to ${plural(started.endpoints.length, "pilot endpoint")}. All other endpoints stay on v${expectedGeneration}.`,
                });
                await Promise.all([reloadVersions(def), loadPilotStates(def, started.releaseId), reloadFleet(def)]);
            }
        } catch (error) {
            if (error?.grpcCode === "ABORTED") {
                patchAgent(def.id, { pendingSubmission: null, choosing: false });
                try {
                    const refreshed = await getManagedAgentConfig(def.vendor);
                    if (!refreshed.currentVersion?.document) throw new Error("Current policy was absent");
                    const landed = JSON.parse(refreshed.currentVersion.document);
                    const merge = mergeManagedConfigDocuments(currentDocument, candidate, landed);
                    patchAgent(def.id, (state) => ({
                        ...state,
                        knownDocuments: {
                            ...state.knownDocuments,
                            ...(current?.versionId && current?.document ? { [current.versionId]: current.document } : {}),
                            [refreshed.currentVersion.versionId]: refreshed.currentVersion.document,
                        },
                        current: refreshed.currentVersion,
                        enabled: refreshed.enabled,
                        controls: def.fromDocument(merge.merged, state.catalog),
                    }));
                    setMessage({
                        tone: "warning",
                        text: merge.conflicts.length
                            ? `Policy v${refreshed.currentVersion.generation} was saved while you edited. Your draft now builds on it. Review the settings you both changed: ${merge.conflicts.join(", ")}.`
                            : `Policy v${refreshed.currentVersion.generation} was saved while you edited. Its other changes are now in your draft. Review them before you start a pilot.`,
                    });
                    await Promise.all([reloadVersions(def), reloadFleet(def)]);
                } catch {
                    setMessage({ tone: "danger", text: "A newer policy landed, but it could not be loaded for merge. Reload before starting a pilot again." });
                }
            } else if (error?.grpcCode === "FAILED_PRECONDITION") {
                patchAgent(def.id, { pendingSubmission: null });
                try {
                    const result = await getManagedAgentConfigRelease(def.vendor);
                    const open = openRelease(result.release);
                    if (open) {
                        adoptRelease(def, open);
                        setMessage({ tone: "warning", text: "Another pilot started first. Review it before you change this policy." });
                        await Promise.all([loadPilotStates(def, open.releaseId), reloadFleet(def)]);
                    } else {
                        const limit = result.pilotEndpointLimit || agent.pilotEndpointLimit;
                        setMessage({ tone: "danger", text: limit ? `The pilot could not start with these endpoints. Select between 1 and ${limit} endpoints of this tenant.` : "The pilot could not start with these endpoints. Select at least one endpoint of this tenant." });
                    }
                } catch {
                    setMessage({ tone: "danger", text: "The pilot could not start. Reload and try again." });
                }
            } else {
                setMessage({ tone: "warning", text: "The pilot response is unknown. Retry without editing to recover the original request." });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleEnabled = async () => {
        const def = definition;
        setSaving(true);
        setMessage(null);
        try {
            const result = await setManagedAgentConfigEnabled(def.vendor, !enabled);
            patchAgent(def.id, (state) => ({
                ...state,
                enabled: result.enabled,
                fleet: state.fleet ? { ...state.fleet, deliveryEnabled: result.enabled } : state.fleet,
            }));
            setMessage({ tone: result.enabled ? "good" : "warning", text: result.enabled ? "Delivery resumed. Endpoints will continue converging." : "Delivery paused. Endpoints that already applied this policy keep it." });
        } catch {
            setMessage({ tone: "danger", text: "Delivery could not be changed. Try again." });
        } finally { setSaving(false); }
    };

    // Confirmed in the Releases view's stop-managing panel. Returns true when
    // the removal generation was stored.
    const handleUnmanage = async () => {
        const def = definition;
        if (!current) return false;
        const expectedGeneration = current.generation;
        const submission = agent.pendingRemoval?.expectedGeneration === expectedGeneration
            ? agent.pendingRemoval
            : { expectedGeneration, saveNonce: createSaveNonce() };
        patchAgent(def.id, { pendingRemoval: submission });
        setSaving(true);
        setMessage(null);
        try {
            const result = await unmanageManagedAgentConfig(def.vendor, submission);
            if (result.version) {
                patchAgent(def.id, (state) => ({
                    ...state,
                    current: result.version,
                    controls: def.fromDocument({}, state.catalog),
                    knownDocuments: { ...state.knownDocuments, [result.version.versionId]: result.version.document },
                    pendingRemoval: null,
                }));
                setMessage({ tone: "good", text: result.idempotentReplay ? "The original removal succeeded. Its response was recovered." : `The tenant policy is being removed from reporting endpoints (v${result.version.generation}).` });
                await Promise.all([reloadVersions(def), reloadFleet(def)]);
                return true;
            }
        } catch (error) {
            if (error?.grpcCode === "ABORTED") patchAgent(def.id, { pendingRemoval: null });
            setMessage({ tone: "danger", text: error?.grpcCode === "ABORTED" ? "The policy changed before removal. Reload and review the current version." : "Policy removal failed. Retry to recover an unknown response safely." });
        } finally {
            setSaving(false);
        }
        return false;
    };

    // Restore loads a stored generation into the draft. It reaches endpoints
    // only through a pilot and a signature, as any other draft does.
    const handleRestore = async (version) => {
        const def = definition;
        let stored = agent.knownDocuments[version.versionId]
            ?? (version.generation === current?.generation ? current?.document : "");
        if (!stored) {
            try {
                stored = (await getManagedAgentConfigVersion(def.vendor, version.versionId))?.document;
                if (!stored) throw new Error("Historical document was absent");
                const loaded = stored;
                patchAgent(def.id, (state) => ({ ...state, knownDocuments: { ...state.knownDocuments, [version.versionId]: loaded } }));
            } catch {
                setMessage({ tone: "danger", text: "This stored policy could not be loaded. Try again." });
                return;
            }
        }
        let document;
        try { document = JSON.parse(stored); }
        catch {
            setMessage({ tone: "danger", text: "The stored policy could not be displayed as controls." });
            return;
        }
        patchAgent(def.id, (state) => ({
            ...state,
            controls: def.fromDocument(document, state.catalog),
            pendingSubmission: null,
            choosing: false,
        }));
        setRejections([]);
        setView("policy");
        setMessage({ tone: "good", text: `The v${version.generation} settings are loaded as the draft. Start a pilot to release them.` });
    };

    const refreshRelease = async (def = definition) => {
        try {
            const result = await getManagedAgentConfigRelease(def.vendor);
            const open = openRelease(result.release);
            if (open) {
                patchAgent(def.id, { release: open });
                await Promise.all([loadPilotStates(def, open.releaseId), reloadFleet(def)]);
                return;
            }
            patchAgent(def.id, { release: null, pilotStates: [], editorDuringPilot: false });
            await reloadCurrent(def);
            await Promise.all([reloadVersions(def), reloadFleet(def)]);
            setMessage({ tone: "warning", text: "This pilot is no longer open. The page shows the current version." });
        } catch {
            setMessage({ tone: "warning", text: "The pilot could not be refreshed. Try again." });
        }
    };

    const updatePilotEndpoint = async (endpointId, action, reason = "") => {
        const def = definition;
        const releaseId = release?.releaseId;
        if (!releaseId) return;
        setSaving(true);
        setMessage(null);
        try {
            const result = await updateManagedAgentConfigReleaseEndpoint(def.vendor, { releaseId, endpointId, action, reason });
            if (result.release) patchAgent(def.id, { release: result.release });
            if (action === RELEASE_ENDPOINT_ACTION.ADD || action === RELEASE_ENDPOINT_ACTION.EXCLUDE || action === RELEASE_ENDPOINT_ACTION.RESTORE) {
                await Promise.all([loadPilotStates(def, releaseId), reloadFleet(def)]);
            }
        } catch (error) {
            if (error?.grpcCode === "FAILED_PRECONDITION") await refreshRelease(def);
            else setMessage({ tone: "danger", text: "The pilot endpoint could not be updated. Try again." });
        } finally {
            setSaving(false);
        }
    };

    const handleSign = async () => {
        const def = definition;
        if (!release) return;
        const generation = release.version?.generation;
        setSaving(true);
        setMessage(null);
        try {
            await signManagedAgentConfigRelease(def.vendor, {
                releaseId: release.releaseId,
                documentSha256: release.version?.documentSha256 ?? "",
                expectedGeneration: current?.generation ?? "0",
                attested: true,
            });
            patchAgent(def.id, { release: null, pilotStates: [], editorDuringPilot: false });
            await reloadCurrent(def);
            await Promise.all([reloadVersions(def), reloadFleet(def)]);
            setMessage({ tone: "good", text: `v${generation} is the tenant policy. Endpoints receive it at their next check-in.` });
        } catch (error) {
            if (error?.grpcCode === "ABORTED") {
                setMessage({ tone: "danger", text: "The tenant policy changed after the pilot started. Reload and review the current version." });
            } else if (error?.grpcCode === "FAILED_PRECONDITION") {
                await refreshRelease(def);
                setMessage({ tone: "warning", text: "The pilot changed before it was signed. Check the pilot endpoints and sign again." });
            } else {
                setMessage({ tone: "warning", text: "The signature response is unknown. Refresh the pilot before you sign again." });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAbandon = async () => {
        const def = definition;
        if (!release) return;
        const generation = release.version?.generation;
        const base = release.baseGeneration;
        const restores = Boolean(base) && base !== "0";
        if (!globalThis.confirm?.(restores
            ? `Abandon the v${generation} pilot? All endpoints go back to the v${base} settings.`
            : `Abandon the v${generation} pilot? All endpoints go back to having no tenant policy.`)) return;
        const submission = agent.pendingAbandon?.releaseId === release.releaseId
            ? agent.pendingAbandon
            : { releaseId: release.releaseId, saveNonce: createSaveNonce() };
        patchAgent(def.id, { pendingAbandon: submission });
        setSaving(true);
        setMessage(null);
        try {
            const result = await abandonManagedAgentConfigRelease(def.vendor, submission);
            patchAgent(def.id, { release: null, pilotStates: [], pendingAbandon: null, editorDuringPilot: false });
            await reloadCurrent(def);
            await Promise.all([reloadVersions(def), reloadFleet(def)]);
            setMessage({
                tone: "good",
                text: result.idempotentReplay
                    ? "The original abandon request succeeded. Its response was recovered."
                    : restores
                        ? `The pilot was abandoned. All endpoints are going back to the v${base} settings (saved as v${result.version?.generation}).`
                        : `The pilot was abandoned. All endpoints are going back to having no tenant policy (saved as v${result.version?.generation}).`,
            });
        } catch (error) {
            if (error?.grpcCode === "FAILED_PRECONDITION") {
                patchAgent(def.id, { pendingAbandon: null });
                await refreshRelease(def);
            } else {
                setMessage({ tone: "warning", text: "The abandon response is unknown. Retry to recover the original request." });
            }
        } finally {
            setSaving(false);
        }
    };

    const step = currentReleaseStep({ release: openRelease(release), choosing: agent.choosing });
    const steps = releaseSteps({
        release: openRelease(release),
        choosing: agent.choosing,
        draftChanges: piloting ? pilotTally.total : tally.total,
        selectedCount: agent.pilotSelection.length,
        targetCount: preview ? preview.reachable : null,
    });
    const showRelease = piloting && !agent.editorDuringPilot;
    const pilotGeneration = release?.version?.generation;
    const currentGeneration = current?.generation ?? "0";
    const startDisabled = saving || !changedKeys.length || !catalog || !preview || preview.rejections.length > 0 || incomplete.length > 0;
    const removal = removalStatus(current, agent.fleet);

    // Refresh pilot evidence while the pilot table is on screen, so a delivery or
    // apply shows without a manual refresh. Skipped while the browser tab is hidden.
    const pilotReleaseId = view === "policy" && showRelease ? release?.releaseId ?? "" : "";
    useEffect(() => {
        if (!pilotReleaseId) return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState === "visible") loadPilotStates(definition, pilotReleaseId, { quiet: true });
        }, PILOT_REFRESH_MS);
        return () => clearInterval(timer);
    }, [definition, pilotReleaseId]);

    // A first pilot has no current version, but delivery still gates it.
    const delivery = (current || piloting) && canManage ? (
        <div className="flex items-center gap-[8px]">
            <StatusMark tone={piloting ? enabled ? "good" : "warning" : removal?.tone ?? (enabled ? "good" : "warning")}>{piloting ? enabled ? "Delivery active" : "Delivery paused" : removal?.label ?? (enabled ? "Delivery active" : "Delivery paused")}</StatusMark>
            <button type="button" className={SECONDARY_ACTION} disabled={saving} onClick={handleEnabled}>{enabled ? "Pause" : "Resume"}</button>
        </div>
    ) : null;

    const title = piloting ? (
        <TitleRow
            eyebrow={definition.label}
            title={`Pilot v${pilotGeneration}`}
            subtitle={current?.unmanage ? `All other endpoints should have no policy (removed in v${current.generation}). Only pilot endpoints should run v${pilotGeneration}.` : ""}
            fields={[
                { label: "Drafted by", value: formatAuthor(release.version?.createdByEmail) },
                ...(release.baseGeneration && release.baseGeneration !== "0" ? [{ label: "Based on", value: `v${release.baseGeneration}` }] : []),
                { label: "Changes", value: plural(pilotTally.total, "setting") },
            ]}
            delivery={delivery}
        />
    ) : (
        <TitleRow
            title={`Draft new ${definition.label} policy`}
            subtitle={current
                ? current.signedByEmail
                    ? `Replaces v${current.generation}, signed by ${current.signedByEmail}`
                    : `Replaces v${current.generation}, saved by ${formatAuthor(current.createdByEmail)}`
                : ""}
            delivery={delivery}
        />
    );

    const editorProps = {
        controls,
        updateControls,
        catalog,
        canManage: canEdit && !agent.loading,
        derived,
        ui: agent.ui,
        setUi,
        onOpenCategory: setCategory,
        onMessage: setMessage,
    };

    return (
        <>
            <div className="min-w-0" aria-busy={agentId !== "all" && agent.loading ? "true" : "false"}>
                <div className="grid gap-[14px]">
                    <div className="flex flex-wrap items-end justify-between gap-[8px] border-b border-[color:var(--line-strong)] pb-[10px]">
                        <TabRow label="Agents" tabs={[{ id: "all", label: "All agents" }, ...agents.map((item) => ({ id: item.id, label: item.label }))]} active={agentId} onChange={selectAgent} />
                        {agentId !== "all" ? <TabRow label="Agent policy views" tabs={VIEWS.map((item) => ({ id: item.id, label: item.label(agent.fleet) }))} active={view} onChange={setView} /> : null}
                    </div>

                    {agentId === "all" ? <AllAgentsOverview agents={agents} onNavigate={navigateFromOverview} /> : <>

                    {message ? (
                        <div className={`rounded-[7px] border p-[10px] text-[11px] ${message.tone === "good" ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]" : message.tone === "danger" ? "border-[#aa4c43] bg-[#aa4c43]/10" : "border-[#aa742f] bg-[#aa742f]/10"}`}>
                            {message.text}
                        </div>
                    ) : null}
                    {rejections.length ? (
                        <div className="grid gap-[6px] rounded-[7px] border border-[#aa4c43] bg-[#aa4c43]/10 p-[10px]">
                            {rejections.map((item, index) => <p key={`${item.pointer}-${index}`} className="m-0 text-[10px]"><code>{item.pointer || "Document"}</code>: {REJECTION_COPY[item.reason] ?? "The server refused this value."}</p>)}
                        </div>
                    ) : null}

                    {view === "policy" ? (
                        <div className="grid gap-[14px]">
                            {/* Until the release is read the page cannot tell a draft from a
                                pilot, so the title and steps hold their place unlit. */}
                            {agent.loading ? <TitleRow title={<PendingLine className="w-[260px] max-w-full" label={`Loading the ${definition.label} policy…`} />} subtitle={<PendingLine className="w-[200px] max-w-full" />} /> : title}
                            <StepStrip
                                steps={agent.loading ? steps.map((item) => ({ ...item, state: "todo", detail: "" })) : steps}
                                selectable={piloting ? [1, step] : agent.choosing ? [1] : []}
                                viewing={piloting ? (agent.editorDuringPilot ? 1 : step) : undefined}
                                onSelect={(number) => {
                                    if (piloting) patchAgent(definition.id, { editorDuringPilot: number === 1 });
                                    else if (number === 1) patchAgent(definition.id, { choosing: false });
                                }}
                            />
                            {agent.loading ? <EditorLoading definition={definition} category={agent.category} controls={controls} /> : showRelease ? (
                                <PilotRelease
                                    definition={definition}
                                    release={release}
                                    states={agent.pilotStates}
                                    statesLoading={agent.pilotStatesLoading}
                                    canManage={canManage}
                                    signerEmail={user?.email ?? ""}
                                    targetCount={preview ? preview.reachable : null}
                                    tally={pilotTally}
                                    pilotDocument={pilotDocument}
                                    busy={saving}
                                    onRefresh={() => refreshRelease()}
                                    onConfirm={(endpoint, confirmed) => updatePilotEndpoint(endpoint.endpointId, confirmed ? RELEASE_ENDPOINT_ACTION.CONFIRM : RELEASE_ENDPOINT_ACTION.UNCONFIRM)}
                                    onExclude={(endpoint, reason) => updatePilotEndpoint(endpoint.endpointId, RELEASE_ENDPOINT_ACTION.EXCLUDE, reason)}
                                    onRestore={(endpoint) => updatePilotEndpoint(endpoint.endpointId, RELEASE_ENDPOINT_ACTION.RESTORE)}
                                    onAdd={(endpoint) => updatePilotEndpoint(endpoint.id, RELEASE_ENDPOINT_ACTION.ADD)}
                                    onAbandon={handleAbandon}
                                    onSign={handleSign}
                                />
                            ) : !piloting && agent.choosing && canManage ? (
                                <ChoosePilotEndpoints
                                    selected={agent.pilotSelection}
                                    onSelectedChange={(pilotSelection) => patchAgent(definition.id, { pilotSelection, pendingSubmission: null })}
                                    limit={agent.pilotEndpointLimit}
                                    preview={preview}
                                    currentGeneration={currentGeneration}
                                    busy={saving}
                                    onBack={() => patchAgent(definition.id, { choosing: false })}
                                    onStart={handleStartPilot}
                                />
                            ) : (
                                <PolicyEditor
                                    definition={definition}
                                    category={agent.category}
                                    onCategoryChange={setCategory}
                                    tally={piloting ? pilotTally : tally}
                                    editorProps={editorProps}
                                    tallyProps={{
                                        currentGeneration,
                                        incomplete,
                                        preview,
                                        removedPointers: controls.removedPointers,
                                        candidate,
                                        showDocument,
                                        onToggleDocument: () => setShowDocument((value) => !value),
                                        canManage,
                                        lockedReason: piloting ? `Sign or abandon the v${pilotGeneration} pilot to edit this policy.` : "",
                                        startDisabled,
                                        onStartPilot: () => patchAgent(definition.id, { choosing: true }),
                                    }}
                                />
                            )}
                        </div>
                    ) : null}

                    {view === "fleet" ? (
                        <FleetCompliance
                            fleet={agent.fleet}
                            fleetLoading={fleetLoading || agent.loading}
                            onRefresh={() => reloadFleet(definition, true)}
                            fleetCategory={fleetCategory}
                            setFleetCategory={setFleetCategory}
                            endpoints={endpoints}
                            endpointsLoading={endpointsLoading}
                            endpointCursor={endpointCursor}
                            onLoadMore={loadMoreEndpoints}
                            expandedEndpointId={expandedEndpointId}
                            setExpandedEndpointId={setExpandedEndpointId}
                            currentDocument={currentDocument}
                            agentLabel={definition.label}
                            configProvider={definition.configProvider}
                            policyFile={definition.policyFile}
                            observedLayer={definition.observedLayer}
                            shadowScope={definition.shadowScope}
                            unmanage={Boolean(current?.unmanage)}
                            pilot={openRelease(release) ? {
                                generation: String(release.version?.generation ?? ""),
                                document: pilotDocument,
                                endpointIds: new Set(release.endpoints.map((endpoint) => endpoint.endpointId)),
                            } : null}
                        />
                    ) : null}

                    {view === "releases" ? agent.loading ? <ReleasesLoading /> : (
                        <ReleasesView
                            definition={definition}
                            history={agent.history}
                            current={current}
                            openRelease={openRelease(release)}
                            knownDocuments={agent.knownDocuments}
                            expandedVersionId={expandedVersionId}
                            onToggleVersion={toggleHistoricalVersion}
                            historyDocumentLoading={historyDocumentLoading}
                            historyDocumentErrors={historyDocumentErrors}
                            releaseDetails={releaseDetails}
                            canManage={canManage}
                            busy={saving}
                            enabled={enabled}
                            signerEmail={user?.email ?? ""}
                            onRestore={handleRestore}
                            onStopManaging={handleUnmanage}
                        />
                    ) : null}
                    </>}
                </div>
            </div>
        </>
    );
}
